import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import expenseService from '../services/expenseService';
import toast from 'react-hot-toast';
import {
  HiOutlineCash,
  HiOutlinePlusCircle,
  HiOutlineRefresh,
  HiOutlineTrendingUp,
  HiOutlineTrendingDown,
  HiOutlineChartBar,
  HiOutlinePencil,
  HiOutlineTrash,
} from 'react-icons/hi';
import './Expenses.css';

const PAYMENT_METHODS = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'tarjeta', label: 'Tarjeta' },
];

const Expenses = () => {
  const { hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('list'); // list, summary, profit
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [profitData, setProfitData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({
    category_id: '',
    date_from: '',
    date_to: '',
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [form, setForm] = useState({
    category_id: '',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_method: 'efectivo',
    reference_number: '',
    supplier_name: '',
    notes: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [expRes, catRes] = await Promise.all([
        expenseService.getAll(filters),
        expenseService.getCategories(),
      ]);
      setExpenses(expRes.data || []);
      setExpenseTotal(expRes.total || 0);
      setCategories(catRes.data || []);

      // Cargar resumen y ganancia
      const [sumRes, profRes] = await Promise.all([
        expenseService.getSummary(filters).catch(() => null),
        expenseService.getProfit(filters).catch(() => null),
      ]);
      setSummaryData(sumRes);
      setProfitData(profRes);
    } catch {
      toast.error('Error cargando gastos');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const handleFilter = () => {
    fetchData();
  };

  const resetForm = () => {
    setForm({
      category_id: '',
      description: '',
      amount: '',
      expense_date: new Date().toISOString().split('T')[0],
      payment_method: 'efectivo',
      reference_number: '',
      supplier_name: '',
      notes: '',
    });
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (expense) => {
    setForm({
      category_id: expense.category_id || '',
      description: expense.description || '',
      amount: expense.amount || '',
      expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : '',
      payment_method: expense.payment_method || 'efectivo',
      reference_number: expense.reference_number || '',
      supplier_name: expense.supplier_name || '',
      notes: expense.notes || '',
    });
    setEditingId(expense.id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (!payload.category_id) delete payload.category_id;

      if (editingId) {
        await expenseService.update(editingId, payload);
        toast.success('Gasto actualizado');
      } else {
        await expenseService.create(payload);
        toast.success('Gasto registrado');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try {
      await expenseService.delete(id);
      toast.success('Gasto eliminado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatMoney = (n) => Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

  return (
    <div className="expenses-page">
      <div className="page-header">
        <div>
          <h1>Gastos</h1>
          <p>Control de gastos y rentabilidad del negocio</p>
        </div>
        <div className="header-actions">
          {hasRole('admin', 'accountant') && (
            <button className="btn-primary" onClick={openCreate}>
              <HiOutlinePlusCircle /> Nuevo Gasto
            </button>
          )}
          <button className="btn-icon" onClick={fetchData} title="Actualizar">
            <HiOutlineRefresh />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="expense-tabs">
        <button className={`expense-tab ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')}>
          <HiOutlineCash /> Gastos
        </button>
        <button className={`expense-tab ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveTab('summary')}>
          <HiOutlineChartBar /> Por Categoría
        </button>
        <button className={`expense-tab ${activeTab === 'profit' ? 'active' : ''}`} onClick={() => setActiveTab('profit')}>
          <HiOutlineTrendingUp /> Ganancia Neta
        </button>
      </div>

      {/* Resumen cards - siempre visible */}
      {profitData && (
        <div className="expense-summary-grid">
          <div className="expense-summary-card revenue-total">
            <span className="summary-label">Ingresos</span>
            <span className="summary-value positive">RD$ {formatMoney(profitData.revenue?.total)}</span>
          </div>
          <div className="expense-summary-card expenses-total">
            <span className="summary-label">Gastos</span>
            <span className="summary-value negative">RD$ {formatMoney(profitData.expenses?.total)}</span>
          </div>
          <div className="expense-summary-card profit-total">
            <span className="summary-label">Ganancia Neta</span>
            <span className={`summary-value ${profitData.net_profit >= 0 ? 'positive' : 'negative'}`}>
              RD$ {formatMoney(profitData.net_profit)}
            </span>
          </div>
          <div className="expense-summary-card margin-total">
            <span className="summary-label">Margen</span>
            <span className={`summary-value ${Number(profitData.margin) >= 0 ? 'positive' : 'negative'}`}>
              {profitData.margin}%
            </span>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="expense-filters">
        <label className="filter-label">
          Categoría
          <select value={filters.category_id} onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}>
            <option value="">Todas</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <label className="filter-label">
          Desde
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        </label>
        <label className="filter-label">
          Hasta
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
        </label>
        <button className="btn-primary" onClick={handleFilter} style={{ alignSelf: 'flex-end' }}>Filtrar</button>
      </div>

      {loading ? (
        <div className="loading-state"><div className="loader"></div><p>Cargando...</p></div>
      ) : (
        <>
          {/* TAB: Lista de gastos */}
          {activeTab === 'list' && (
            <>
              {expenses.length === 0 ? (
                <div className="empty-state-card">
                  <HiOutlineCash className="empty-icon" />
                  <p>No hay gastos registrados</p>
                </div>
              ) : (
                <div className="table-card">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Descripción</th>
                        <th>Categoría</th>
                        <th>Proveedor</th>
                        <th>Método</th>
                        <th>Monto</th>
                        {hasRole('admin', 'accountant') && <th>Acciones</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((exp) => (
                        <tr key={exp.id}>
                          <td className="date-cell">{formatDate(exp.expense_date)}</td>
                          <td>
                            <div>{exp.description}</div>
                            {exp.reference_number && <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Ref: {exp.reference_number}</span>}
                          </td>
                          <td><span className="status-badge">{exp.category_name || 'Sin categoría'}</span></td>
                          <td>{exp.supplier_name || '-'}</td>
                          <td><span className={`payment-badge ${exp.payment_method}`}>{exp.payment_method}</span></td>
                          <td className="mono-cell total-cell" style={{ color: '#ef4444' }}>
                            RD$ {formatMoney(exp.amount)}
                          </td>
                          {hasRole('admin', 'accountant') && (
                            <td>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button className="btn-pay" onClick={() => openEdit(exp)} title="Editar">
                                  <HiOutlinePencil />
                                </button>
                                {hasRole('admin') && (
                                  <button className="btn-delete-sm" onClick={() => handleDelete(exp.id)} title="Eliminar">
                                    <HiOutlineTrash />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="grand-total">
                        <td colSpan="5" style={{ textAlign: 'right', fontWeight: 700 }}>Total del período:</td>
                        <td className="mono-cell" style={{ color: '#ef4444', fontWeight: 700 }}>
                          RD$ {formatMoney(expenseTotal)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}

          {/* TAB: Por categoría */}
          {activeTab === 'summary' && summaryData && (
            <div>
              <div className="category-grid">
                {(summaryData.by_category || []).map((cat) => (
                  <div className="category-card" key={cat.category}>
                    <div className="category-info">
                      <span className="category-name">{cat.category}</span>
                      <span className="category-count">{cat.count} gasto(s)</span>
                    </div>
                    <span className="category-amount">RD$ {formatMoney(cat.total)}</span>
                  </div>
                ))}
              </div>
              {(summaryData.by_category || []).length === 0 && (
                <div className="empty-state-card">
                  <HiOutlineChartBar className="empty-icon" />
                  <p>No hay datos para el período seleccionado</p>
                </div>
              )}
            </div>
          )}

          {/* TAB: Ganancia Neta */}
          {activeTab === 'profit' && profitData && (
            <div className="detail-grid">
              <div className="detail-card">
                <h3><HiOutlineTrendingUp style={{ color: '#16a34a' }} /> Ingresos</h3>
                <div className="info-grid">
                  <div className="info-row">
                    <span className="info-label">Subtotal (sin ITBIS)</span>
                    <span className="info-value mono">RD$ {formatMoney(profitData.revenue?.subtotal)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">ITBIS cobrado</span>
                    <span className="info-value mono">RD$ {formatMoney(profitData.revenue?.tax)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Total Ingresos</span>
                    <span className="info-value mono" style={{ fontWeight: 700, color: '#16a34a' }}>RD$ {formatMoney(profitData.revenue?.total)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Órdenes cerradas</span>
                    <span className="info-value">{profitData.revenue?.order_count || 0}</span>
                  </div>
                </div>
              </div>

              <div className="detail-card">
                <h3><HiOutlineTrendingDown style={{ color: '#ef4444' }} /> Egresos</h3>
                <div className="info-grid">
                  <div className="info-row">
                    <span className="info-label">Total Gastos</span>
                    <span className="info-value mono" style={{ fontWeight: 700, color: '#ef4444' }}>RD$ {formatMoney(profitData.expenses?.total)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Cantidad de gastos</span>
                    <span className="info-value">{profitData.expenses?.count || 0}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Comisiones pagadas</span>
                    <span className="info-value mono">RD$ {formatMoney(profitData.commissions?.total_paid)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-card" style={{ gridColumn: '1 / -1' }}>
                <h3><HiOutlineChartBar style={{ color: '#3b82f6' }} /> Resultado</h3>
                <div className="info-grid">
                  <div className="info-row">
                    <span className="info-label">Ingresos Totales</span>
                    <span className="info-value mono" style={{ color: '#16a34a' }}>+ RD$ {formatMoney(profitData.revenue?.total)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Gastos Totales</span>
                    <span className="info-value mono" style={{ color: '#ef4444' }}>- RD$ {formatMoney(profitData.expenses?.total)}</span>
                  </div>
                  <div className="info-row" style={{ borderTop: '2px solid #e2e8f0', paddingTop: 12 }}>
                    <span className="info-label" style={{ fontWeight: 700, fontSize: '0.9rem' }}>Ganancia Neta</span>
                    <span className="info-value mono" style={{ fontWeight: 700, fontSize: '1.2rem', color: profitData.net_profit >= 0 ? '#16a34a' : '#ef4444' }}>
                      RD$ {formatMoney(profitData.net_profit)}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Margen de ganancia</span>
                    <span className="info-value mono" style={{ fontWeight: 600 }}>{profitData.margin}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal crear/editar gasto */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <h2>{editingId ? 'Editar Gasto' : 'Registrar Gasto'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="expense-form-grid">
                <div className="form-group">
                  <label>Categoría</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select style={{ flex: 1 }} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                      <option value="">Seleccionar...</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {hasRole('admin') && (
                      <button type="button" className="btn-icon" title="Nueva categoría" onClick={() => setShowCategoryModal(true)}
                        style={{ minWidth: 38, height: 38, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#14532d', fontWeight: 700 }}>
                        +
                      </button>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Monto (RD$) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Descripción *</label>
                  <input
                    type="text"
                    required
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Ej: Compra de arroz 50 sacos"
                  />
                </div>
                <div className="form-group">
                  <label>Fecha</label>
                  <input
                    type="date"
                    value={form.expense_date}
                    onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Método de Pago</label>
                  <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Proveedor</label>
                  <input
                    type="text"
                    value={form.supplier_name}
                    onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                    placeholder="Nombre del proveedor"
                  />
                </div>
                <div className="form-group">
                  <label>No. Referencia / Factura</label>
                  <input
                    type="text"
                    value={form.reference_number}
                    onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                    placeholder="Ej: FAC-001234"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Notas</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Notas adicionales..."
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary">
                  {editingId ? 'Actualizar' : 'Registrar Gasto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal crear categoría */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2>Nueva Categoría de Gasto</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!newCategoryName.trim()) return;
              try {
                const res = await expenseService.createCategory(newCategoryName.trim());
                setCategories([...categories, res.data].sort((a, b) => a.name.localeCompare(b.name)));
                setForm({ ...form, category_id: String(res.data.id) });
                setShowCategoryModal(false);
                setNewCategoryName('');
                toast.success('Categoría creada');
              } catch (err) {
                toast.error(err.response?.data?.error || 'Error al crear categoría');
              }
            }}>
              <div className="form-group">
                <label>Nombre de la categoría *</label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Ej: Materiales de limpieza"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => { setShowCategoryModal(false); setNewCategoryName(''); }}>Cancelar</button>
                <button type="submit" className="btn-primary">Crear Categoría</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
